/**
 * `POST /v1/auth/staff/login` e `POST /v1/auth/staff/shift/start|end` — a autenticacao do
 * STAFF (CONTRACTS §8.2 bloco "Auth", API §6.1, SECURITY §5.1 e §9).
 *
 * O que separa este arquivo do `public/auth.ts` nao e a forma, e o modelo de ameaca. O balcao
 * e uma tela publica num PC compartilhado: a sessao dura o TURNO (12 h), bloqueia por
 * inatividade em 15 min, e o cookie sai com `SameSite=Strict`, porque o build interno nao tem
 * nenhum fluxo legitimo de chegada cross-site — nao ha link de WhatsApp que abra o Painel
 * (SECURITY §7.4, ADR-0017).
 *
 * **Step-up nao ganhou rota.** `CONTRACTS §8.2` fecha o bloco Auth em 6 linhas e nenhuma delas
 * e `/auth/step-up`. Reautenticar e literalmente o que ADR-0009 §5.5 define como step-up
 * ("reautenticação (OTP/senha) nos últimos 10 min"), entao `POST /v1/auth/staff/login`
 * **com sessao ativa do mesmo sujeito** faz step-up na sessao existente em vez de abrir uma
 * segunda. Uma pessoa, uma sessao; e o campo `stepUpAt` da resposta diz ate quando vale.
 */
import { z } from 'zod';

import { StaffRoleSchema } from '../enums';
import { InstantSchema, Uuidv7Schema } from '../common';

// ---------------------------------------------------------------------------
// POST /v1/auth/staff/login
// ---------------------------------------------------------------------------

/**
 * TOTP: 6 digitos. `string` e nao `number` pelo mesmo motivo do OTP — o zero a esquerda.
 * Opcional no schema e **obrigatorio na regra** para os 5 papeis de
 * `STAFF_ROLE_TOTP_REQUIRED_VALUES`: quem decide e o caso de uso, que conhece os papeis do
 * usuario; o schema nao pode saber quem esta logando antes de ler a senha.
 */
export const TotpCodeSchema = z
  .string()
  .regex(/^\d{6}$/, 'O código do autenticador tem 6 dígitos.');
export type TotpCode = z.infer<typeof TotpCodeSchema>;

export const StaffLoginRequestSchema = z
  .object({
    /** E-mail. `citext` no banco (`staff_user.email`), entao a comparacao ignora caixa. */
    email: z.string().email('E-mail inválido.').max(254),
    /**
     * Comprimento minimo e politica, nao capricho (ADR-0009: "política de senha por
     * comprimento"). O maximo existe para que uma senha de 1 MB nao vire um ataque de custo
     * de CPU contra o hash — que e caro por desenho.
     */
    password: z.string().min(12, 'A senha precisa ter ao menos 12 caracteres.').max(256),
    totpCode: TotpCodeSchema.optional(),
  })
  .strict();
export type StaffLoginRequest = z.infer<typeof StaffLoginRequestSchema>;

/** Um vinculo de `staff_store_membership`, projetado para o wire. */
export const StaffMembershipSchema = z
  .object({
    storeId: Uuidv7Schema,
    role: StaffRoleSchema,
  })
  .strict();
export type StaffMembership = z.infer<typeof StaffMembershipSchema>;

/**
 * `200 OK`. Sem token no corpo — vale o mesmo que para o cliente (ADR-0009).
 *
 * `stepUpAt` viaja porque a UI do Painel precisa saber se o botao de estorno acima do limite
 * abre a caixa de reautenticacao ou executa direto. E informacao derivada de estado do
 * servidor, nao credencial: saber que alguem se reautenticou as 14h03 nao autoriza nada.
 */
export const StaffLoginResponseSchema = z
  .object({
    staff: z
      .object({
        id: Uuidv7Schema,
        /** Papeis de TODOS os vinculos. A matriz de SECURITY §4.2 e avaliada por papel. */
        roles: z.array(StaffRoleSchema),
        memberships: z.array(StaffMembershipSchema),
      })
      .strict(),
    csrfToken: z.string().min(32),
    /** Fim da sessao por TTL absoluto — 12 h (CONTRACTS §5.4). */
    expiresAt: InstantSchema,
    /** Ultimo step-up. O login com TOTP conta como step-up; sem TOTP, e `null`. */
    stepUpAt: InstantSchema.nullable(),
    /** `true` quando o turno ainda nao foi aberto — a UI leva para a escolha de filial. */
    shiftRequired: z.boolean(),
  })
  .strict();
export type StaffLoginResponse = z.infer<typeof StaffLoginResponseSchema>;

// ---------------------------------------------------------------------------
// POST /v1/auth/staff/shift/start | /end
// ---------------------------------------------------------------------------

/**
 * Abrir turno amarra a sessao a UMA filial. E tambem "o gesto que destrava o audio do painel"
 * (API §6.1, P2 da auditoria 03) — o navegador so permite tocar som apos interacao do usuario,
 * e este e o clique que existe de qualquer forma.
 *
 * A filial vem no corpo e nao num header: API §1.2 recusa `X-Store-Id` explicitamente
 * ("header implicito vira chave de cache esquecida e preco errado exibido").
 */
export const ShiftStartRequestSchema = z
  .object({
    storeId: Uuidv7Schema,
  })
  .strict();
export type ShiftStartRequest = z.infer<typeof ShiftStartRequestSchema>;

export const ShiftStartResponseSchema = z
  .object({
    storeId: Uuidv7Schema,
    startedAt: InstantSchema,
    /** TTL absoluto do turno — 12 h a partir do login, nao da abertura do turno. */
    expiresAt: InstantSchema,
    /** Inatividade que bloqueia a tela: 900 s (SECURITY §9, tabela de controles). */
    idleLockSeconds: z.literal(900),
    /** O par do double-submit muda: abrir turno ROTACIONA a sessao (fixacao de sessao). */
    csrfToken: z.string().min(32),
  })
  .strict();
export type ShiftStartResponse = z.infer<typeof ShiftStartResponseSchema>;

/**
 * Encerrar turno **revoga a sessao**, nao apenas desamarra a filial. SECURITY §9: "logout no
 * fim do turno". Deixar a sessao viva sem filial seria uma credencial de 12 h abandonada num
 * PC de balcao — o risco fisico que o modelo inteiro existe para reduzir.
 */
export const ShiftEndResponseSchema = z
  .object({
    endedAt: InstantSchema,
    revokedSessions: z.number().int().nonnegative(),
  })
  .strict();
export type ShiftEndResponse = z.infer<typeof ShiftEndResponseSchema>;
